/**
 * SnowballSliders — Interactive parameter controls with live debounced re-simulation.
 *
 * Per the playbook P13 spirit: "Slider for salary surplus; Stress test panel for
 * X-month disruption." The sliders re-call /snowball/simulate as the user drags so
 * they can SEE the impact on sweet-spot month and year-20 wealth in real time.
 */
import { useEffect, useRef, useState } from "react";
import { inr } from "../../lib/format";
import { Sliders } from "lucide-react";

export default function SnowballSliders({ baseRequest, onResult, onBusy }) {
  // Deltas applied to the base request; live slider state.
  const [delta, setDelta] = useState({
    salary_pct: 0,
    fund_pct: 0,
    rent_pct: 0,
    appreciation_pp: 0,
    rate_pp: 0,
  });

  // Keep latest callback refs so the effect doesn't re-fire when parent
  // re-renders with new function identities. Setters from useState ARE stable
  // but the parent passes either setOverlayRes or anonymous functions — we
  // route through refs to be safe and to make the effect deps minimal.
  const onResultRef = useRef(onResult);
  const onBusyRef = useRef(onBusy);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onBusyRef.current = onBusy; }, [onBusy]);

  // Debounced re-simulate — depends ONLY on delta and baseRequest.
  useEffect(() => {
    if (!baseRequest) return;
    const allZero = Object.values(delta).every((v) => v === 0);
    if (allZero) {
      onResultRef.current?.(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        onBusyRef.current?.(true);
        const tweaked = {
          ...baseRequest,
          monthly_salary_surplus: Math.max(
            0,
            baseRequest.monthly_salary_surplus * (1 + delta.salary_pct / 100)
          ),
          starting_snowball_fund: Math.max(
            0,
            baseRequest.starting_snowball_fund * (1 + delta.fund_pct / 100)
          ),
          properties: baseRequest.properties.map((p) => ({
            ...p,
            monthly_rent: Math.round(p.monthly_rent * (1 + delta.rent_pct / 100)),
            annual_appreciation_pct: Math.max(
              0,
              Math.min(25, p.annual_appreciation_pct + delta.appreciation_pp)
            ),
            loan_rate_pct: Math.max(
              4,
              Math.min(20, p.loan_rate_pct + delta.rate_pp)
            ),
          })),
        };
        const api = (await import("../../lib/api")).default;
        const { data } = await api.post("/snowball/simulate", tweaked);
        onResultRef.current?.(data);
      } catch (e) {
        // Silent — keep showing baseline. Errors here are non-fatal
        // (most commonly transient 5xx during rapid slider drags).
      } finally {
        onBusyRef.current?.(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [delta, baseRequest]);

  const reset = () =>
    setDelta({ salary_pct: 0, fund_pct: 0, rent_pct: 0, appreciation_pp: 0, rate_pp: 0 });

  return (
    <div className="card-flat p-5" data-testid="snowball-sliders">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sliders size={16} className="text-[#C85A32]" />
          <h4 className="font-serif text-lg">Live what-if controls</h4>
        </div>
        <button
          onClick={reset}
          className="text-xs text-muted-foreground hover:text-foreground underline"
          data-testid="snowball-sliders-reset"
        >
          Reset
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Drag any slider to see your sweet spot, wealth and velocity move in real time.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <DeltaSlider label="Salary surplus" value={delta.salary_pct} onChange={(v) => setDelta({ ...delta, salary_pct: v })} min={-50} max={100} step={5} suffix="%" testid="slider-salary" />
        <DeltaSlider label="Starting snowball fund" value={delta.fund_pct} onChange={(v) => setDelta({ ...delta, fund_pct: v })} min={-100} max={200} step={10} suffix="%" testid="slider-fund" />
        <DeltaSlider label="Rent across all properties" value={delta.rent_pct} onChange={(v) => setDelta({ ...delta, rent_pct: v })} min={-30} max={50} step={5} suffix="%" testid="slider-rent" />
        <DeltaSlider label="Appreciation (everywhere)" value={delta.appreciation_pp} onChange={(v) => setDelta({ ...delta, appreciation_pp: v })} min={-5} max={8} step={0.5} suffix="pp" testid="slider-apprec" />
        <DeltaSlider label="Loan rate (everywhere)" value={delta.rate_pp} onChange={(v) => setDelta({ ...delta, rate_pp: v })} min={-2} max={4} step={0.25} suffix="pp" testid="slider-rate" />
      </div>
    </div>
  );
}

function DeltaSlider({ label, value, onChange, min, max, step, suffix, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-mono font-semibold ${value > 0 ? "text-emerald-700" : value < 0 ? "text-rose-700" : "text-foreground"}`}>
          {value > 0 ? "+" : ""}{value}{suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-[#e5e1d6] rounded-lg appearance-none cursor-pointer accent-[#C85A32]"
      />
    </div>
  );
}
